// src/App.js
import React, { useEffect, useCallback, useRef, useState } from 'react';
import { doc, setDoc, updateDoc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebaseConfig";
import { BrowserRouter, HashRouter, Routes, Route } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from "@capacitor/local-notifications";
import { postMissedCallNotification, clearAllMissedCallNotifications } from "./utils/missedCallNotifications";
import { Preferences } from '@capacitor/preferences';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import VerifyPhone from './pages/VerifyPhone';
import VerifyId from './pages/VerifyId';
import VerifyNewEmailChange from './pages/VerifyNewEmailChange';
import SavedChats from './pages/SavedChats';
import Notes from './pages/Notes';
import Fieldbook from './pages/Fieldbook';
import ConfirmNewEmail from './pages/ConfirmNewEmail';
import Messaging from './pages/Messaging';
import BoloPage from './pages/BoloPage';
import AdminModerationPage from "./pages/AdminModerationPage";
import MyAccount from './pages/MyAccount';
import MyActivityPage from "./pages/MyActivityPage";
import MyNetworkPage from "./pages/MyNetworkPage";
import UserProfile from './pages/UserProfile';
import ProfilePage from './pages/ProfilePage';
import DirectChatRoute from './pages/DirectChatRoute';
import GroupChatRoute from './pages/GroupChatRoute';
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Splash from "./pages/Splash";
import DownloadPage from "./pages/DownloadPage";
import NewsPage from './pages/NewsPage';
import { UserProvider, useUser } from './context/UserContext';
import { App as CapacitorApp } from '@capacitor/app';
import { useNavigate, useLocation } from 'react-router-dom';
import TheWirePage from './pages/TheWirePage';
import { Toast } from '@capacitor/toast';
import IncomingCallManager from "./components/IncomingCallManager";
import GlobalCallShell from "./components/GlobalCallShell";
import GlobalIncomingCallOverlay from "./components/GlobalIncomingCallOverlay";
import { getOrCreateDirectConversation } from "./firebase/messagingUtils";
import { useCallSession } from "./context/CallSessionContext";
import { reportCallEnded } from "./utils/callKitBridge";
import {
  clearPendingNotificationRedirect,
  getPendingNotificationRedirect,
  getPostVerificationRoute,
  isFreshPendingNotificationRedirect,
  isPublicAuthPath,
} from './utils/authVerification';



import { BackButtonHandler, PushActionHandler, NativeCallHandler } from './components/AppListeners';

const RouterImpl = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter;

function AuthVerificationGate({ currentUser, profileExists }) {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  useEffect(() => {
    hasRedirected.current = false;
  }, [location.pathname]);

  useEffect(() => {
    const path = location.pathname;
    if (isPublicAuthPath(path)) return;
    if (hasRedirected.current) return;

    const authUser = auth.currentUser;
    if (!authUser) {
      hasRedirected.current = true;
      navigate('/login', { replace: true });
      return;
    }

    if (profileExists === false) {
      localStorage.removeItem('currentUserProfile');
      hasRedirected.current = true;
      navigate('/login', { replace: true });
      return;
    }

    if (!authUser.emailVerified) {
      localStorage.setItem('pendingEmail', authUser.email || '');
      hasRedirected.current = true;
      navigate('/verify-email', { replace: true });
      return;
    }

    let cachedProfile = null;
    try {
      cachedProfile = JSON.parse(localStorage.getItem('currentUserProfile') || 'null');
    } catch (_) {}

    const resolvedPhoneVerified =
      currentUser?.phoneVerified === true ||
      cachedProfile?.phoneVerified === true;

    if (!resolvedPhoneVerified) {
      hasRedirected.current = true;
      navigate('/verify-phone', { replace: true });
      return;
    }

    const vs =
      currentUser?.verificationStatus ||
      cachedProfile?.verificationStatus;
    if (!vs || vs !== 'approved') {
      hasRedirected.current = true;
      navigate('/verify-id', { replace: true });
      return;
    }

    const sessionAcknowledged =
      sessionStorage.getItem('sessionAcknowledged') === 'true';
    const isSplashRoute = path === '/splash';
    if (!sessionAcknowledged && !isSplashRoute) {
      hasRedirected.current = true;
      navigate('/splash', { replace: true });
    }
  }, [currentUser, location.pathname, navigate, profileExists]);

  return null;
}

function PendingNotificationRedirector({ currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const authUser = auth.currentUser;
    if (!authUser?.uid) return;

    const path = location.pathname;
    if (path === '/' || path === '/login') return;

    const pendingRedirect = getPendingNotificationRedirect(authUser.uid);
    if (!pendingRedirect) return;

    if (!isFreshPendingNotificationRedirect(pendingRedirect)) {
      clearPendingNotificationRedirect();
      return;
    }

    const pendingRoute = pendingRedirect.route;

    if (getPostVerificationRoute(authUser, currentUser) !== '/notes') {
      return;
    }

    if (sessionStorage.getItem('sessionAcknowledged') !== 'true') {
      return;
    }

    if (path === pendingRoute) {
      clearPendingNotificationRedirect();
      return;
    }

    clearPendingNotificationRedirect();
    navigate(pendingRoute, { replace: true });
  }, [currentUser, location.pathname, navigate]);

  return null;
}

export default function App() {
  const { currentUser, profileExists } = useUser();
  const callSession = useCallSession();
  const [connectingCall, setConnectingCall] = useState(null);
  const currentUserRef = useRef(currentUser);
  const callSessionRef = useRef(null);
  const lastPushTokenRef = useRef(null);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  // Keep latest callSession available to push handlers without
  // re-running registration on every call state change.
  useEffect(() => {
    callSessionRef.current = callSession;
  }, [callSession]);


  useEffect(() => {
    if (Capacitor.getPlatform() === 'ios') {
      PushNotifications.setBadgeCount(0);
    }
  }, []);


  useEffect(() => {
    const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
    document.body.classList.add(platform);

    return () => {
      document.body.classList.remove(platform);
    };
  }, []);


  // Clear iOS badge on app active transitions to keep OS badge in sync
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return;

    const clearBadge = () => {
      PushNotifications.setBadgeCount(0);
    };

    clearBadge();

    const sub = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        clearBadge();
      }
    });

    return () => {
      sub.remove();
    };
  }, []);

  // Register native action buttons for local incoming call notifications.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    LocalNotifications.registerActionTypes({
      types: [
        {
          id: 'INCOMING_CALL',
          actions: [
            { id: 'accept', title: 'Accept' },
            { id: 'decline', title: 'Decline', destructive: true },
          ],
        },
      ],
    }).catch(() => { });
  }, []);

  // Centralized app visibility metadata for the call engine.
  // Invariant: setAppVisibility must NEVER directly change call state,
  // routing, or notifications; it is lifecycle metadata only.

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const visibilityHandler = ({ isActive }) => {
      // ⚡ FIX: Use ref to avoid re-binding listener on every context update
      const session = callSessionRef.current;
      const setVisibility = session?.actions?.setAppVisibility;

      if (!setVisibility) return;
      const next = isActive ? 'foreground' : 'background';
      setVisibility(next);
    };

    const sub = CapacitorApp.addListener('appStateChange', visibilityHandler);

    return () => {
      if (sub && typeof sub.remove === 'function') {
        sub.remove();
      }
    };
  }, []); // ✅ Stable dependency array (empty) prevents infinite addListener/removeListener loop

  // If a notification timer expired while the app was closed, flip duty status and clear the timer
  useEffect(() => {
    const timerExpiresAt = currentUser?.notificationPrefs?.timerExpiresAt;
    if (!currentUser?.uid || !timerExpiresAt) return;

    const expiresMs = new Date(timerExpiresAt).getTime();
    if (Number.isNaN(expiresMs) || expiresMs > Date.now()) return;

    const newStatus = currentUser.dutyStatus === 'on-duty' ? 'off-duty' : 'on-duty';

    updateDoc(doc(db, 'users', currentUser.uid), {
      dutyStatus: newStatus,
      'notificationPrefs.timer': null,
      'notificationPrefs.timerExpiresAt': null,
    }).then(() => {
      console.log('Duty status auto-switched after timer expiration.');
    }).catch((err) => {
      console.error('Failed to auto-switch duty status after timer expiration:', err);
    });
  }, [currentUser]);

  // One-tap accept / decline for background calls using local notification actions.
  // This does NOT change WebRTC ordering; it only routes actions into the call engine.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const sub = LocalNotifications.addListener(
      'localNotificationActionPerformed',
      async (event) => {
        const actionId = event?.actionId;
        const notification = event?.notification || {};
        const extra = notification.extra || {};
        const callId =
          extra.callId ||
          extra.callID ||
          extra.call_id ||
          null;

        if (extra?.type === 'missed_call') {
          await clearAllMissedCallNotifications();
          return;
        }

        if (!callId) return;
        if (!actionId || (actionId !== 'accept' && actionId !== 'decline')) {
          return;
        }

        const session = callSessionRef.current;
        const state = session?.state || {};
        const actions = session?.actions || {};

        if (!actions) return;

        const isCurrentIncoming =
          state.incomingCallId === callId &&
          state.callState === 'ringing_incoming' &&
          !state.inCall;

        if (!isCurrentIncoming) {
          return;
        }

        if (actionId === 'accept') {
          try {
            await actions.acceptCall();
          } catch (err) {
            console.warn(
              '[LocalNotifications] acceptCall from notification failed:',
              err
            );
          }
        } else if (actionId === 'decline') {
          try {
            await actions.declineCall();
          } catch (err) {
            console.warn(
              '[LocalNotifications] declineCall from notification failed:',
              err
            );
          }
        }
      }
    );

    return () => {
      sub?.remove && sub.remove();
    };
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let registrationListener;
    let registrationErrorListener;
    let receivedListener;
    let actionListener;
    let iosTokenRetryTimeout;

    let setupStarted = false;
    const getIosFcmToken = async () => {
      const { value } = await Preferences.get({ key: 'pushToken' });
      return value;
    };

    const saveToken = async (pushTokenValue) => {
      const user = currentUserRef.current;
      if (!user?.uid || !pushTokenValue) return;
      try {
        await setDoc(
          doc(db, "users", user.uid),
          { pushToken: pushTokenValue, fcmToken: pushTokenValue },
          { merge: true }
        );
        console.log('Token saved to Firestore');
      } catch (error) {
        console.error('Failed to save token:', error);
      }
    };

    const setupPush = async () => {
      if (setupStarted) return;
      setupStarted = true;
      const permission = await PushNotifications.requestPermissions();
      if (permission.receive === 'granted') {
        await PushNotifications.register();
      }

      registrationListener = await PushNotifications.addListener('registration', async (token) => {
        const platform = Capacitor.getPlatform();
        let pushTokenValue = token.value;

        // iOS returns the APNs token here; prefer the FCM token saved by native
        if (platform === 'ios') {
          const iosFcmToken = await getIosFcmToken();
          if (!iosFcmToken) {
            console.warn('iOS registration returned APNs token; waiting for FCM token from Firebase Messaging.');
            return;
          }
          pushTokenValue = iosFcmToken;
        }

        console.log('Push registration success:', pushTokenValue);

        lastPushTokenRef.current = pushTokenValue;
        await saveToken(pushTokenValue);
      });

      registrationErrorListener = await PushNotifications.addListener('registrationError', (err) => {
        console.error('Push registration error:', err);
      });

      receivedListener = await PushNotifications.addListener('pushNotificationReceived', async (notification) => {
        console.log('Push received:', notification);

        if (notification?.data?.type === 'incoming_call') {
          console.log('Incoming call notification.');
          return;
        }

        if (notification?.data?.type === 'call_status') {
          const status = notification?.data?.status;
          const callId = notification?.data?.callId;
          const terminal = ["no_answer", "declined", "ended", "failed"];
          const session = callSessionRef.current;
          const activeCallId = session?.state?.activeCallId || null;

          if (callId && terminal.includes(status)) {
            if (!activeCallId || activeCallId === callId) {
              reportCallEnded(callId, status === "no_answer" ? "unanswered" : status);
            }
          }

          if (status === "no_answer") {
            const calleeId = notification?.data?.calleeId;
            if (!calleeId || calleeId === currentUser?.uid) {
              const callerName = notification?.data?.callerName || "Unknown";
              const callerRank = notification?.data?.callerRank || "";
              const callerAgency = notification?.data?.callerAgency || "";
              const callerLabel = `${callerRank ? `${callerRank} ` : ""}${callerName}${
                callerAgency ? ` (${callerAgency})` : ""
              }`;
              await postMissedCallNotification({ callId, callerLabel });
            }
          }
        }

        const prefs = currentUser?.notificationPrefs || {};

        if (notification.data?.type === 'message') {
          if (prefs.messaging === true) {
            console.log('Message notification received (prefs allow).');
          } else {
            console.log('Skipping message notification: prefs block it.');
          }
          return;
        }

        if (notification.data?.type === 'bolo') {
          if (prefs.bolo !== true) {
            console.log('Skipping BOLO notification: prefs block it.');
            return;
          }
          const channelId = 'bolo';

          await LocalNotifications.schedule({
            notifications: [
              {
                title: notification.title || 'BOLO Alert',
                body: notification.body || JSON.stringify(notification.data),
                id: Date.now(),
                schedule: { at: new Date(Date.now() + 1000) },
                channelId,
                sound: 'default',
                smallIcon: 'ic_stat_name',
              },
            ],
          });

          console.log('BOLO notification forced (foreground + background)');
          return;
        }
      });

      // Handle taps on push notifications (including incoming calls) in one place.
      actionListener = await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        async (event) => {
          const data = event?.notification?.data || {};

          // Cold-start / restored incoming call hydration:
          // - Only hydrate calls that are still ringing and have an offer.
          // - Never hydrate if the engine is already busy or already has this callId.
          if (data.type === 'incoming_call') {
            const callId =
              data.callId || data.call_id || data.callID || null;

            if (!callId) {
              console.warn(
                '[Push] incoming_call action without callId; skipping hydration'
              );
              return;
            }

            const session = callSessionRef.current;
            const setIncomingCallData = session?.actions?.setIncomingCallData;
            const engineState = session?.state || {};

            if (!setIncomingCallData) {
              return;
            }

            // If the engine is not idle (busy ending/connected/etc),
            // ignore hydration. Visibility of calls must stay
            // strictly single-call at a time.
            if (engineState.callState && engineState.callState !== 'idle') {
              return;
            }

            // Idempotent: if we already have this call active or incoming, skip.
            if (
              engineState.inCall ||
              engineState.callState === 'connected' ||
              engineState.incomingCallId === callId ||
              engineState.activeCallId === callId
            ) {
              return;
            }

            try {
              const callRef = doc(db, 'calls', callId);
              const snap = await getDoc(callRef);
              if (!snap.exists()) return;

              const callData = snap.data();
              if (!callData) return;

              // Validity gate: only hydrate still-ringing calls that have an offer.
              if (
                callData.status !== 'ringing' ||
                !callData.offer
              ) {
                return;
              }

              console.log(
                '[Push] Hydrated incoming_call into engine',
                callId
              );

              setIncomingCallData({
                id: snap.id,
                ...callData,
              });
            } catch (err) {
              console.warn(
                '[Push] Failed to hydrate incoming_call from notification action:',
                err
              );
            }
          }
        }
      );
    };

    setupPush();
    if (Capacitor.getPlatform() === 'ios') {
      getIosFcmToken().then((storedToken) => {
        if (storedToken) {
          lastPushTokenRef.current = storedToken;
          saveToken(storedToken);
        }
      });
      iosTokenRetryTimeout = setTimeout(() => {
        getIosFcmToken().then((maybeToken) => {
          if (maybeToken) {
            lastPushTokenRef.current = maybeToken;
            saveToken(maybeToken);
          }
        });
      }, 3000);
    }

    return () => {
      registrationListener?.remove();
      registrationErrorListener?.remove();
      receivedListener?.remove();
      actionListener?.remove();
      if (iosTokenRetryTimeout) clearTimeout(iosTokenRetryTimeout);
    };
  }, []);

  useEffect(() => {
    const token = lastPushTokenRef.current;
    if (!token) return;
    if (!currentUser?.uid) return;
    setDoc(
      doc(db, "users", currentUser.uid),
      { pushToken: token, fcmToken: token },
      { merge: true }
    ).catch((error) => {
      console.error('Failed to save token:', error);
    });
  }, [currentUser]);


  // Register SW only on web (native apps use Capacitor Push, not web SW)
  useEffect(() => {
    if (!Capacitor.isNativePlatform() && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('✅ Service Worker registered with scope:', registration.scope);
        })
        .catch((err) => {
          console.error('❌ Service Worker registration failed:', err);
        });
    }
  }, []);









  return (
    <RouterImpl>
      <AuthVerificationGate
        currentUser={currentUser}
        profileExists={profileExists}
      />
      <PendingNotificationRedirector currentUser={currentUser} />
      {connectingCall && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.75)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          fontSize: 18,
          textAlign: 'center',
          padding: '16px'
        }}>
          Connecting call...
        </div>
      )}
      <NativeCallHandler onConnectingChange={setConnectingCall} />
      <BackButtonHandler />
      <PushActionHandler />
      <IncomingCallManager currentUser={currentUser} />
      <GlobalCallShell />
      <GlobalIncomingCallOverlay />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-phone" element={<VerifyPhone />} />
        <Route path="/verify-id" element={<VerifyId />} />
        <Route path="/verify-new-email-change" element={<VerifyNewEmailChange />} />
        <Route path="/splash" element={<Splash />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/saved" element={<Layout><SavedChats /></Layout>} />
        <Route path="/notes" element={<Layout><Notes /></Layout>} />
        <Route path="/confirm-new-email" element={<ConfirmNewEmail />} />
        <Route path="/database" element={<Layout><Fieldbook /></Layout>} />
        <Route path="/fieldbook" element={<Layout><Fieldbook /></Layout>} />
        <Route path="/messages" element={<Layout><Messaging /></Layout>} />
        <Route path="/bolo" element={<Layout><BoloPage /></Layout>} />
        <Route path="/thewire" element={<Layout><TheWirePage /></Layout>} />
        <Route path="/news" element={<Layout><NewsPage /></Layout>} />
        <Route path="/myactivity" element={<Layout><MyActivityPage /></Layout>} />
        <Route path="/mynetwork" element={<Layout><MyNetworkPage /></Layout>} />
        <Route path="/moderation" element={<Layout><AdminModerationPage /></Layout>} />
        <Route path="/my-account" element={<Layout><MyAccount /></Layout>} />
        <Route path="/direct/:chatId" element={<Layout><DirectChatRoute /></Layout>} />
        <Route path="/group/:groupId" element={<Layout><GroupChatRoute /></Layout>} />
        <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />
        <Route path="/profile/:userId" element={<Layout><UserProfile /></Layout>} />
      </Routes>
    </RouterImpl>
  );
}
